@if (visible) {
  <section
    class="card bg-white border border-slate-200/60 shadow-sm p-6 space-y-5 print:hidden mb-4"
    data-testid="agreement-quality-overview"
    aria-labelledby="agreement-quality-heading"
  >
    <div class="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
      <div class="space-y-1">
        <h2 id="agreement-quality-heading" class="text-lg font-bold text-slate-800">
          Agreement & Quality
        </h2>
        <p class="text-sm text-slate-500 max-w-3xl">
          Agreement statistics were calculated from independent reviewer decisions before
          moderation. Different stages and question types use different agreement measures.
        </p>
      </div>
      @if (overview) {
        <div class="flex flex-wrap gap-2 text-xs font-semibold text-slate-600">
          <span class="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1">
            Screening stages measured: {{ screeningStagesMeasured }}
          </span>
          <span class="rounded-md border border-slate-200 bg-slate-50 px-2.5 py-1">
            Metrics below threshold: {{ metricsBelowThreshold }}
          </span>
        </div>
      }
    </div>

    @if (loading) {
      <div class="grid gap-3 md:grid-cols-4" data-testid="agreement-quality-loading">
        @for (item of [1, 2, 3, 4]; track item) {
          <div class="animate-pulse rounded-xl border border-slate-200 bg-slate-50 p-4">
            <div class="h-4 w-28 rounded bg-slate-200"></div>
            <div class="mt-4 h-8 w-20 rounded bg-slate-200"></div>
            <div class="mt-3 h-4 w-full rounded bg-slate-100"></div>
          </div>
        }
      </div>
    } @else if (errorMessage) {
      <div class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        <p>{{ errorMessage }}</p>
        <button
          type="button"
          class="mt-3 rounded-md border border-red-300 bg-white px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-red-300"
          aria-label="Retry loading agreement and quality statistics"
          (click)="retry()"
        >
          Retry
        </button>
      </div>
    } @else if (overview; as o) {
      <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        <article class="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-500">
            Stage 2 - Title/Abstract Screening
          </h3>
          @if (o.titleAbstractScreening.availability === 'AVAILABLE') {
            <p class="mt-3 text-2xl font-black text-slate-900">
              &kappa; {{ formatKappa(o.titleAbstractScreening.cohenKappa) }}
            </p>
            <p class="text-sm font-semibold text-slate-700">
              {{ interpretationLabel(o.titleAbstractScreening.interpretation) }}
            </p>
            <p class="mt-3 text-sm text-slate-600">
              {{ formatPercent(o.titleAbstractScreening.percentAgreement) }} agreement
            </p>
            <p class="text-sm text-slate-600">
              {{ formatInteger(o.titleAbstractScreening.pairedDecisionCount) }} paired decisions
            </p>
            <p class="mt-2 text-xs font-semibold text-slate-700">
              {{ thresholdStatusLabel(o.titleAbstractScreening.thresholdStatus) }}
            </p>
          } @else {
            <p class="mt-4 text-sm font-semibold text-slate-700">Legacy project</p>
            <p class="mt-1 text-xs text-slate-500">No official snapshot is available.</p>
          }
        </article>

        <article class="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-500">
            Stage 4 - Full-Text Screening
          </h3>
          @if (o.fullTextScreening.availability === 'AVAILABLE') {
            <p class="mt-3 text-2xl font-black text-slate-900">
              &kappa; {{ formatKappa(o.fullTextScreening.cohenKappa) }}
            </p>
            <p class="text-sm font-semibold text-slate-700">
              {{ interpretationLabel(o.fullTextScreening.interpretation) }}
            </p>
            <p class="mt-3 text-sm text-slate-600">
              {{ formatPercent(o.fullTextScreening.percentAgreement) }} agreement
            </p>
            <p class="mt-2 text-xs font-semibold text-slate-700">
              {{ thresholdStatusLabel(o.fullTextScreening.thresholdStatus) }}
            </p>
          } @else {
            <p class="mt-4 text-sm font-semibold text-slate-700">Legacy project</p>
            <p class="mt-1 text-xs text-slate-500">No official snapshot is available.</p>
          }
        </article>

        <article class="rounded-xl border border-slate-200 bg-slate-50/70 p-4">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-500">
            Stage 5 - Data Extraction
          </h3>
          @if (o.dataExtraction.availability === 'AVAILABLE') {
            <p class="mt-3 text-2xl font-black text-slate-900">
              {{ formatPercent(o.dataExtraction.overallExactAgreementPercentage) }}
            </p>
            <p class="text-sm font-semibold text-slate-700">overall exact agreement</p>
            <p class="mt-3 text-sm text-slate-600">
              {{ formatInteger(o.dataExtraction.totalPairedAnswers) }} paired answers
            </p>
            <p class="text-sm text-slate-600">
              {{ formatInteger(o.dataExtraction.totalQuestions) }} questions analyzed
            </p>
          } @else {
            <p class="mt-4 text-sm font-semibold text-slate-700">Legacy project</p>
            <p class="mt-1 text-xs text-slate-500">No official snapshot is available.</p>
          }
        </article>
      </div>

      <button
        type="button"
        class="text-xs font-semibold text-secondary hover:underline focus:outline-none focus:ring-2 focus:ring-secondary/40 rounded"
        aria-label="View agreement and quality details"
        (click)="detailsOpen = true"
      >
        View details
      </button>
    } @else {
      <div class="rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
        No agreement and quality statistics are available.
      </div>
    }
  </section>
}

@if (overview; as o) {
  <app-base-modal
    [open]="detailsOpen"
    (openChange)="detailsOpen = $event"
    title="Agreement & Quality Details"
    size="5xl"
  >
    <div class="space-y-6" data-testid="agreement-quality-details">
      <p class="rounded-lg border border-blue-100 bg-blue-50 p-3 text-sm text-blue-900">
        Cohen's kappa is used for categorical screening decisions and categorical extraction
        questions. Multi-choice, numeric and free-text extraction questions use question-type
        specific agreement measures.
      </p>

      <div class="grid gap-3 md:grid-cols-2">
        @for (stage of [o.titleAbstractScreening, o.fullTextScreening]; track stage.stage) {
          <section class="rounded-xl border border-slate-200 p-4">
            <div class="flex items-start justify-between gap-3">
              <div>
                <h3 class="text-sm font-bold text-slate-900">{{ screeningTitle(stage) }}</h3>
                @if (stage.finalizedAt) {
                  <p class="mt-1 text-xs text-slate-500">
                    Finalized on {{ finalizedAt(stage.finalizedAt) }}
                  </p>
                }
              </div>
              <span class="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
                {{ thresholdStatusLabel(stage.thresholdStatus) }}
              </span>
            </div>
            @if (stage.availability === 'AVAILABLE') {
              <dl class="mt-4 grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt class="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Cohen's kappa
                  </dt>
                  <dd class="font-bold text-slate-900">{{ formatKappa(stage.cohenKappa) }}</dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Agreement
                  </dt>
                  <dd class="font-bold text-slate-900">
                    {{ formatPercent(stage.percentAgreement) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Paired decisions
                  </dt>
                  <dd class="font-bold text-slate-900">
                    {{ formatInteger(stage.pairedDecisionCount) }}
                  </dd>
                </div>
                <div>
                  <dt class="text-xs font-semibold uppercase tracking-wide text-slate-400">
                    Interpretation
                  </dt>
                  <dd class="font-bold text-slate-900">
                    {{ interpretationLabel(stage.interpretation) }}
                  </dd>
                </div>
              </dl>
              @if (screeningReasonLabel(stage)) {
                <p class="mt-3 text-xs text-amber-800">{{ screeningReasonLabel(stage) }}</p>
              }
            } @else {
              <p class="mt-4 text-sm text-slate-600">No official snapshot is available.</p>
            }
          </section>
        }
      </div>

      <section class="rounded-xl border border-slate-200 p-4">
        <div class="flex items-start justify-between gap-3">
          <div>
            <h3 class="text-sm font-bold text-slate-900">Stage 5 - Data Extraction</h3>
            @if (o.dataExtraction.finalizedAt) {
              <p class="mt-1 text-xs text-slate-500">
                Finalized on {{ finalizedAt(o.dataExtraction.finalizedAt) }}
              </p>
            }
          </div>
          <span class="rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs">
            {{ formatInteger(o.dataExtraction.questions.length) }} question metrics
          </span>
        </div>

        @if (o.dataExtraction.availability === 'AVAILABLE') {
          <div class="mt-4 overflow-x-auto rounded-lg border border-slate-200">
            <table class="min-w-full divide-y divide-slate-200 text-sm">
              <thead
                class="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500"
              >
                <tr>
                  <th class="px-3 py-2">Question</th>
                  <th class="px-3 py-2">Type</th>
                  <th class="px-3 py-2">Paired</th>
                  <th class="px-3 py-2">Missing</th>
                  <th class="px-3 py-2">Metric</th>
                  <th class="px-3 py-2">Result</th>
                  <th class="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-slate-100">
                @for (
                  question of o.dataExtraction.questions;
                  track trackQuestion($index, question)
                ) {
                  <tr>
                    <td class="px-3 py-2 font-medium text-slate-800">
                      {{ questionLabel(question) }}
                    </td>
                    <td class="px-3 py-2 text-slate-600">{{ questionTypeLabel(question) }}</td>
                    <td class="px-3 py-2 text-slate-600">
                      {{ formatInteger(question.pairedAnswerCount) }}
                    </td>
                    <td class="px-3 py-2 text-slate-600">
                      {{ formatInteger(question.unpairedMissingCount) }}
                    </td>
                    <td class="px-3 py-2 text-slate-600">{{ metricLabel(question) }}</td>
                    <td class="px-3 py-2 text-slate-800">
                      {{ extractionMetricResult(question) }}
                    </td>
                    <td class="px-3 py-2 text-slate-600">
                      @if (question.thresholdStatus) {
                        {{ thresholdStatusLabel(question.thresholdStatus) }}
                      } @else if (extractionReasonLabel(question)) {
                        {{ extractionReasonLabel(question) }}
                      } @else {
                        {{ interpretationLabel(question.interpretation) }}
                      }
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        } @else {
          <p class="mt-4 text-sm text-slate-600">No official snapshot is available.</p>
        }
      </section>
    </div>
  </app-base-modal>
}
